/**
 * RESPONSIBILITY: the words above the home page's events strip.
 *
 * The events themselves are the one events list; what is left to edit here is the
 * heading, the button and how many to show.
 *
 * No @EditableBy, so WRITE defaults to NONE — admin only.
 */
import { Section, Text, Link, Num } from '@framework/core/decorators';
import { SectionName } from '../section-names';
import { LINK_HINT, COUNT_HINT } from '../hints';

@Section({ name: SectionName.HomeEvents, label: 'Home · Events band' })
export class HomeEventsSection {
  @Text({
    label: 'Subtitle above', maxLength: 120,
    description: 'The small gold line above the title. Leave empty for none.',
  })
  eyebrow!: string;

  @Text({ label: 'Title', maxLength: 120, default: 'Upcoming Events' })
  heading!: string;

  @Text({ label: 'Button text', maxLength: 120, default: 'All events' })
  linkLabel!: string;

  @Link({ label: 'Button link', default: '#events', description: LINK_HINT })
  linkHref!: string;

  /** The requirements doc asks for seven. A field, so six needs no deploy. */
  @Num({
    label: 'How many to show', min: 1, max: 12, integer: true, default: 7,
    description: COUNT_HINT,
  })
  count!: number;

  @Num({
    label: 'Seconds on each card', min: 0, max: 60, integer: true, default: 7,
    group: 'The strip', collapsed: true,
    description: 'How long a card is shown before the next one. Zero leaves it still until somebody presses an arrow.',
  })
  autoSeconds!: number;

  @Num({
    label: 'Seconds after a press before it starts again', min: 0, max: 120, integer: true,
    default: 8, group: 'The strip',
    description: 'It also pauses whenever an arrow has focus, and starts again the moment focus leaves — that needs no waiting.',
  })
  resumeSeconds!: number;

  @Text({
    label: 'Dots', maxLength: 120, default: 'Event', group: 'The strip',
    description:
      'Names each dot under the strip for a screen reader — it is read as "Event 3". '
      + 'The dots themselves show how many there are and which one is on screen.',
  })
  dotsLabel!: string;

  @Text({ label: 'Back arrow', maxLength: 120, default: 'Previous events', group: 'The strip' })
  prevLabel!: string;

  @Text({ label: 'Forward arrow', maxLength: 120, default: 'More events', group: 'The strip' })
  nextLabel!: string;

  @Text({
    label: 'Stop button', maxLength: 120, default: 'Stop moving', group: 'The strip',
    description: 'The control that stops it. It is there because a strip that moves by itself has to be stoppable.',
  })
  pauseLabel!: string;

  @Text({ label: 'Start button', maxLength: 120, default: 'Start moving', group: 'The strip' })
  playLabel!: string;
}
